import { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  fetchSubscribersByTemplate,
  toggleUserCampaign,
  bulkToggleUserCampaigns,
  deleteUserCampaign,
  transformUserCampaignToCamelCase,
} from '../api/supabase/automations';

/**
 * Manages subscriber data and actions for a single campaign template.
 * Only fetches when templateId is provided (drawer is open).
 *
 * @param {string|null} templateId
 */
export function useTemplateSubscribers(templateId) {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  const [bulkProgress, setBulkProgress] = useState(null);
  const abortRef = useRef(false);

  const load = useCallback(async () => {
    if (!templateId) { setSubscribers([]); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSubscribersByTemplate(templateId);
      setSubscribers((data || []).map(transformUserCampaignToCamelCase));
    } catch (err) {
      console.error('[useTemplateSubscribers] load error:', err);
      setError(err.message || 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => { load(); }, [load]);

  const markBusy = (id) => setBusyIds((prev) => new Set(prev).add(id));
  const clearBusy = (id) =>
    setBusyIds((prev) => { const next = new Set(prev); next.delete(id); return next; });

  const subscribeUser = useCallback(
    async (userId) => {
      markBusy(userId);
      try {
        const functions = getFunctions(undefined, 'us-east1');
        const fn = httpsCallable(functions, 'subscribeToCampaign');
        await fn({ templateId, targetUserId: userId });
        await load();
      } finally {
        clearBusy(userId);
      }
    },
    [templateId, load]
  );

  const toggleUser = useCallback(
    async (userCampaignId, isEnabled) => {
      markBusy(userCampaignId);
      try {
        const data = await toggleUserCampaign(userCampaignId, isEnabled);
        const updated = transformUserCampaignToCamelCase(data);
        setSubscribers((prev) =>
          prev.map((s) => (s.id === userCampaignId ? { ...s, isEnabled: updated.isEnabled } : s))
        );
      } finally {
        clearBusy(userCampaignId);
      }
    },
    []
  );

  const unsubscribeUser = useCallback(
    async (userCampaignId) => {
      markBusy(userCampaignId);
      try {
        await deleteUserCampaign(userCampaignId);
        setSubscribers((prev) => prev.filter((s) => s.id !== userCampaignId));
      } finally {
        clearBusy(userCampaignId);
      }
    },
    []
  );

  const bulkSubscribe = useCallback(
    async (userIds) => {
      if (!userIds.length) return { succeeded: 0, failed: 0 };
      abortRef.current = false;
      setBulkProgress({ total: userIds.length, done: 0, failed: 0 });
      let succeeded = 0;
      let failed = 0;
      const functions = getFunctions(undefined, 'us-east1');
      const fn = httpsCallable(functions, 'subscribeToCampaign');

      for (const uid of userIds) {
        if (abortRef.current) break;
        try {
          await fn({ templateId, targetUserId: uid });
          succeeded++;
        } catch {
          failed++;
        }
        setBulkProgress({ total: userIds.length, done: succeeded + failed, failed });
      }

      await load();
      setBulkProgress(null);
      return { succeeded, failed };
    },
    [templateId, load]
  );

  const bulkToggle = useCallback(
    async (userCampaignIds, isEnabled) => {
      if (!userCampaignIds.length) return;
      userCampaignIds.forEach(markBusy);
      try {
        await bulkToggleUserCampaigns(userCampaignIds, isEnabled);
        setSubscribers((prev) =>
          prev.map((s) =>
            userCampaignIds.includes(s.id) ? { ...s, isEnabled } : s
          )
        );
      } finally {
        userCampaignIds.forEach(clearBusy);
      }
    },
    []
  );

  const cancelBulk = useCallback(() => { abortRef.current = true; }, []);

  return {
    subscribers,
    loading,
    error,
    reload: load,
    busyIds,
    bulkProgress,
    subscribeUser,
    toggleUser,
    unsubscribeUser,
    bulkSubscribe,
    bulkToggle,
    cancelBulk,
  };
}
